
import React, { useState } from 'react';
import { Play, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { YouTubeVideo } from '@/services/youtubeVideoService';
import { YouTubeVideoModal } from '../YouTubeVideoModal';

interface VideoCarouselItemProps {
  video: YouTubeVideo;
  onVideoDeleted: () => Promise<void>;
}

export const VideoCarouselItem: React.FC<VideoCarouselItemProps> = ({
  video,
  onVideoDeleted,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleWatch = () => {
    if (video.video_id) {
      setIsModalOpen(true);
    }
  };

  const handleDelete = async () => {
    await onVideoDeleted();
  };

  return (
    <>
      <Card className="overflow-hidden">
        <div className="relative">
          <div 
            className="aspect-video bg-muted cursor-pointer relative overflow-hidden"
            onClick={handleWatch}
          >
            {video.video_id ? (
              <>
                <img
                  src={`https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`}
                  alt={video.title || "YouTube video thumbnail"}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-black/70 p-3 text-white">
                    <Play fill="white" className="h-6 w-6" />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <Play className="h-12 w-12 opacity-20" />
              </div>
            )}
          </div>
          
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-7 w-7 opacity-80 hover:opacity-100"
            onClick={handleDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        
        <CardContent className="p-3">
          <h4 className="font-medium line-clamp-1 text-sm">
            {video.title || "YouTube Video"}
          </h4>
          {video.notes && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
              {video.notes}
            </p>
          )}
        </CardContent>
        
        <CardFooter className="p-2 pt-0">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full gap-1 text-xs h-8"
            onClick={handleWatch}
          >
            <Play className="h-3 w-3" />
            Watch
          </Button>
        </CardFooter>
      </Card>

      {video.video_id && (
        <YouTubeVideoModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          video={video}
        />
      )}
    </>
  );
};
